import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });

// Attach auth token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("auth_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Players
export const getPlayers = (team) =>
  api.get("/players", { params: team ? { team } : {} }).then((r) => r.data);

export const createPlayer = (data) =>
  api.post("/players", data).then((r) => r.data);

export const updatePlayer = (id, data) =>
  api.put(`/players/${id}`, data).then((r) => r.data);

export const deletePlayer = (id) =>
  api.delete(`/players/${id}`).then((r) => r.data);

// Merge two characters (admin only) — moves all raid data/flags/notes from source to target
export const mergePlayers = (targetId, sourceId) =>
  api.post(`/players/${targetId}/merge`, { source_id: sourceId }).then((r) => r.data);

// Raids
export const getRaids = (team) =>
  api.get("/raids", { params: team ? { team } : {} }).then((r) => r.data);

export const createRaid = (data) =>
  api.post("/raids", data).then((r) => r.data);

export const deleteRaid = (id) =>
  api.delete(`/raids/${id}`).then((r) => r.data);

export const deleteRaidsByDate = (date) =>
  api.delete(`/raids/by-date/${date}`).then((r) => r.data);

// Player Raid Data
export const getPlayerData = (params) =>
  api.get("/player-data", { params }).then((r) => r.data);

export const getPlayerRaidData = (playerId, raidId) =>
  api.get(`/player-data/${playerId}/${raidId}`).then((r) => r.data);

export const updatePlayerRaidData = (playerId, raidId, data) =>
  api.put(`/player-data/${playerId}/${raidId}`, data).then((r) => r.data);

export const createPlayerData = (data) =>
  api.post("/player-data", data).then((r) => r.data);

// Import
export const importRPB = (formData) =>
  api.post("/import", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  }).then((r) => r.data);

// Import Combat Log
export const importCombatLog = (formData) =>
  api.post("/import-combat-log", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 120000,
  }).then((r) => r.data);

// Import Data Analysis
export const importDataAnalysis = (formData) =>
  api.post("/import-data-analysis", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  }).then((r) => r.data);

// Unified Import (multi-file)
export const importUnified = (formData) =>
  api.post("/import-unified", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  }).then((r) => r.data);

// Import a brand new raid using ONLY a WarcraftLogs report (no .txt required)
export const importFromWCL = ({ reportCode, team, date, raidName }) =>
  api.post("/import-from-wcl", {
    report_code: reportCode,
    team,
    date: date || "",
    raid_name: raidName || "",
  }).then((r) => r.data);

// Check WCL credentials health (global, no raid)
export const getWclHealth = () => api.get("/wcl/status").then((r) => r.data);

// Re-pull a saved WarcraftLogs report on an existing raid and overwrite
// the stored gear + consumables. Used to repair raids imported before the
// gear-slot-mapping fix landed.
export const refreshRaidFromWcl = (raidId) =>
  api.post(`/raids/${raidId}/refresh-wcl`).then((r) => r.data);

// Update guild profile (admins only)
export const updateGuild = (guildId, fields) =>
  api.put(`/guilds/${guildId}`, fields).then((r) => r.data);

// Negative Overview
export const getNegativeOverview = (raidId) =>
  api.get(`/negative-overview/${raidId}`).then((r) => r.data);

export const dismissIssue = (raidId, playerId, issueType, issueDetail) =>
  api.post(`/negative-overview/${raidId}/dismiss`, {
    player_id: playerId, issue_type: issueType, issue_detail: issueDetail
  }).then((r) => r.data);

export const restoreIssue = (raidId, playerId, issueType, issueDetail) =>
  api.post(`/negative-overview/${raidId}/restore`, {
    player_id: playerId, issue_type: issueType, issue_detail: issueDetail
  }).then((r) => r.data);

// Player Progression
export const getPlayerProgression = (playerId) =>
  api.get(`/player-progression/${playerId}`).then((r) => r.data);

// Stats
export const getStats = () => api.get("/stats").then((r) => r.data);

// Constants
export const getConstants = () => api.get("/constants").then((r) => r.data);

// Bulk Class Update
export const bulkClassUpdate = (updates) =>
  api.post("/players/bulk-class", { updates }).then((r) => r.data);

// Delete Player
export const deletePlayerApi = (id) =>
  api.delete(`/players/${id}`).then((r) => r.data);

// Discord Webhook
export const sendDiscordWebhook = (webhookUrl, raidId, playerName) =>
  api.post("/discord-webhook", { webhook_url: webhookUrl, raid_id: raidId, player_name: playerName || null }).then((r) => r.data);

// Resolve Item/Enchant/Gem Names
export const resolveItems = (itemIds, enchantIds, gemIds) =>
  api.post("/resolve-items", {
    item_ids: itemIds || [],
    enchant_ids: enchantIds || [],
    gem_ids: gemIds || [],
  }).then((r) => r.data);

// Raid Rename
export const updateRaid = (raidId, data) =>
  api.put(`/raids/${raidId}`, data).then((r) => r.data);

// Team Comparison
export const getTeamComparison = (teamA, teamB, raidIdA, raidIdB) =>
  api.get("/team-comparison", { params: { team_a: teamA, team_b: teamB, raid_id_a: raidIdA || undefined, raid_id_b: raidIdB || undefined } }).then((r) => r.data);

export const getRaidComparison = (raidIdA, raidIdB) =>
  api.get("/raid-comparison", { params: { raid_id_a: raidIdA, raid_id_b: raidIdB } }).then((r) => r.data);

export const sendTeamBattleDiscord = (webhookUrl, teamA, teamB) =>
  api.post("/discord-team-battle", { webhook_url: webhookUrl, team_a: teamA, team_b: teamB }).then((r) => r.data);

export const sendRaidComparisonDiscord = (webhookUrl, raidIdA, raidIdB) =>
  api.post("/discord-raid-comparison", { webhook_url: webhookUrl, raid_id_a: raidIdA, raid_id_b: raidIdB }).then((r) => r.data);

// Per-Tab Discord Summary
export const sendTabSummary = (webhookUrl, playerId, raidId, tab) =>
  api.post("/discord-tab-summary", { webhook_url: webhookUrl, player_id: playerId, raid_id: raidId, tab }).then((r) => r.data);

// MVP
export const getMVP = (raidId) =>
  api.get(`/mvp/${raidId}`).then((r) => r.data);

export const sendMVPDiscord = (webhookUrl, raidId) =>
  api.post("/mvp-discord", { webhook_url: webhookUrl, raid_id: raidId }).then((r) => r.data);

// Player Rating
export const getPlayerRating = (playerId) =>
  api.get(`/player-rating/${playerId}`).then((r) => r.data);

// Raid Calendar
export const getRaidCalendar = (team, includeArchived = false) =>
  api.get("/raid-calendar", { params: { team: team || undefined, include_archived: includeArchived } }).then((r) => r.data);

// Archive Raid
export const archiveRaid = (raidId) =>
  api.put(`/raids/${raidId}`, { archived: true }).then((r) => r.data);

export const unarchiveRaid = (raidId) =>
  api.put(`/raids/${raidId}`, { archived: false }).then((r) => r.data);

// Pug Toggle
export const togglePug = (playerId, raidId, isPug) =>
  api.post("/toggle-pug", { player_id: playerId, raid_id: raidId, is_pug: isPug }).then((r) => r.data);

// Player Badges & MVP History
export const getPlayerBadges = (playerId) =>
  api.get(`/player-badges/${playerId}`).then((r) => r.data);

// Per-raid MVP score (velocity chart)
export const getPlayerMvpHistory = (playerId) =>
  api.get(`/player-mvp-history/${playerId}`).then((r) => r.data);

// Role-peer median/avg per attended raid
export const getTeamAverageTrend = (playerId) =>
  api.get(`/team-average-trend/${playerId}`).then((r) => r.data);

// WarcraftLogs
export const fetchWclParses = (raidId, reportCodes) => {
  const codes = Array.isArray(reportCodes) ? reportCodes : [reportCodes];
  return api.post(`/raids/${raidId}/fetch-parses`, { report_codes: codes }).then((r) => r.data);
};

export const getWclStatus = (raidId) =>
  api.get(`/raids/${raidId}/wcl-status`).then((r) => r.data);

// Weekly Report
export const getWeeklyReport = (team, startDate, endDate) =>
  api.get("/weekly-report", { params: { team, start_date: startDate, end_date: endDate } }).then((r) => r.data);

export const sendWeeklyReportDiscord = (webhookUrl, team, startDate, endDate) =>
  api.post("/weekly-report-discord", { webhook_url: webhookUrl, team, start_date: startDate, end_date: endDate }).then((r) => r.data);

// Player Changelog
export const getPlayerChangelog = (playerId) =>
  api.get(`/player-changelog/${playerId}`).then((r) => r.data);

export const sendPlayerChangelogDiscord = (webhookUrl, playerId, startDate, endDate) =>
  api.post("/player-changelog-discord", { webhook_url: webhookUrl, player_id: playerId, start_date: startDate || "", end_date: endDate || "" }).then((r) => r.data);

export const sendRaidChangelogDiscord = (webhookUrl, raidId) =>
  api.post("/raid-changelog-discord", { webhook_url: webhookUrl, raid_id: raidId }).then((r) => r.data);

// Upgrade Flags
export const getUpgradeFlags = (playerId) =>
  api.get(`/upgrade-flags/${playerId}`).then((r) => r.data);

export const createUpgradeFlag = (data) =>
  api.post("/upgrade-flags", data).then((r) => r.data);

export const deleteUpgradeFlag = (flagId) =>
  api.delete(`/upgrade-flags/${flagId}`).then((r) => r.data);

export const getAllUpgradeFlags = () =>
  api.get("/upgrade-flags").then((r) => r.data);

export const sendUpgradeFlagsDiscord = (webhookUrl) =>
  api.post("/upgrade-flags-discord", { webhook_url: webhookUrl }).then((r) => r.data);

// Player Notes
export const getPlayerNotes = (playerId) =>
  api.get(`/player-notes/${playerId}`).then((r) => r.data);

export const createPlayerNote = (playerId, note, weekStart) =>
  api.post("/player-notes", { player_id: playerId, note, week_start: weekStart }).then((r) => r.data);

export const deletePlayerNote = (noteId) =>
  api.delete(`/player-notes/${noteId}`).then((r) => r.data);

export default api;
